import { getProductTemplateBlotterColumn } from '@src/components/OTC/ProductTemplateTooltip';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import type { OTCDealData } from '@src/types/OTC';
import { isFixedRateLoanDeal, isFloatingRateLoanDeal } from '@src/types/OTC';
import { getColumnTitle, getColumnTooltip } from '@src/utils/fieldDefinitionUtils';
import { fixedRateLoanFieldDefinitions } from '@src/utils/OTC/fixedRateLoanFieldDefinitions';
import {
  formatOtcDealBlotterLocalDateTime,
  formatOtcDealBookingTimezoneTooltip,
  getOtcDealLoanAmountDisplayValue,
  getOtcDealSubmitTimeValue,
} from '@src/utils/OTC/otcDealDisplayUtils';
import {
  appendTooltipWhenFeatureDisabled,
  OPEN_TERM_SUPPORT_COMING_SOON,
} from '@src/utils/OTC/otcFeatureFlagTooltipAppend';
import {
  formatHolidayCalendars,
  formatPaymentPeriod,
  formatProductType,
  formatSettlementCycle,
} from '@src/utils/OTC/otcUtils';
import {
  EMPTY_SIZE_VALUE,
  isFixedRateLoanSecurity,
  isFloatingRateLoanSecurity,
  LoanQuoteTerminationTypeEnum,
  useDefaultColumns,
  type Column,
  type ColumnDef,
  type Leaves,
  type OTCFixedRateLoanProductTemplate,
} from '@talos/kyoko';
import type { ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { otcDealUserColumn } from '../otcDealUserColumn';
import {
  getLoanCollateralAssetDisplayValue,
  isUnsecuredLoanDeal,
  LoanCollateralAssetCell,
} from './LoanCollateralAssetCell';

export type LoanColumnMapId = keyof ReturnType<typeof useLoanColumnDefs>;

/**
 * Columns that are visible in the loan blotter and inspector
 * Note that some fields resolve ProductDetails
 * @returns
 */
function getVisibleColumns(): Array<LoanColumnMapId> {
  return [
    'submitTime',
    'productType',
    'originationDate',
    'effectiveDate',
    'status',
    'productTemplate',
    'symbol',
    'borrowedAsset',
    'collateralAsset',
    'pledgedCollateral',
    'term',
    'termType',
    'expirationDate',
    'settlementDate',
    'counterparty',
    'marketAccount',
    'side',
    'loanAmount',
    'rate',
    'paymentPeriod',
    'subAccount',
    'user',
    'settlementCycle',
    'loanId',
    'businessDayConvention',
    'holidayCalendars',
    'timestamp',
  ];
}

export const useLoanColumns = (): Column[] => {
  const mapping = useLoanColumnDefs();

  const columnDefinitions = useMemo(() => {
    return new Map<string, Column>(
      Object.entries(mapping)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, { ...value, id: key }])
    );
  }, [mapping]);

  const visibleColumns = useMemo(() => {
    return getVisibleColumns().filter(item => !!columnDefinitions.get(item));
  }, [columnDefinitions]);

  return useDefaultColumns(visibleColumns, columnDefinitions);
};

export function useLoanColumnDefs() {
  const { enableLoanOpenTermSupport } = useFeatureFlag();

  const columnMapping = useMemo(() => {
    const mapping = {
      submitTime: {
        field: 'SubmitTime',
        title: 'Submit Time',
        type: 'date',
        params: {
          milliseconds: true,
          valueGetter: (params: ValueGetterParams<OTCDealData>) => getOtcDealSubmitTimeValue(params.data),
        },
        initialSort: '-',
        initialWidth: 150,
        initialSortIndex: 0,
        description: 'Timestamp of the Deal Submission',
      },
      productType: {
        field: 'SubProductType',
        title: 'Product Type',
        type: 'text',
        initialWidth: 120,
        description: 'Type of loan product',
        params: {
          getLabel: formatProductType,
        },
      },
      originationDate: {
        field: 'DealInfo.TradeTime',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.tradeDate),
        type: 'date',
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.tradeDate),
        params: {
          customFormatter: formatOtcDealBlotterLocalDateTime,
        },
        tooltipValueGetter: ({ value, data }) =>
          formatOtcDealBookingTimezoneTooltip(value, data?.DealInfo.SettleCutoffTimezone ?? 'UTC'),
      },
      effectiveDate: {
        field: 'DealInfo.EffectiveTime',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.effectiveDate),
        type: 'date',
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.effectiveDate),
        params: {
          customFormatter: formatOtcDealBlotterLocalDateTime,
        },
        tooltipValueGetter: ({ value, data }) =>
          formatOtcDealBookingTimezoneTooltip(value, data?.DealInfo.SettleCutoffTimezone ?? 'UTC'),
      },
      status: {
        field: 'Status',
        type: 'text',
        initialWidth: 80,
        description: 'Status of the loan',
      },
      symbol: {
        field: 'Symbol',
        title: 'Symbol',
        type: 'security',
        initialWidth: 200,
        description: 'Resolved Loan Symbol based on Product Template and Maturity Date',
      },
      borrowedAsset: {
        field: 'Symbol',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.borrowedAsset),
        type: 'currency',
        initialWidth: 100,
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.borrowedAsset),
        params: {
          showDescription: false,
          symbolField: 'Symbol',
          securityCurrencyField: 'ProductDetails.BorrowedAsset',
        },
      },
      collateralAsset: {
        field: 'Symbol',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.collateralAsset),
        type: 'custom',
        initialWidth: 100,
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.collateralAsset),
        params: {
          valueGetter: getLoanCollateralAssetDisplayValue,
          cellRenderer: LoanCollateralAssetCell,
        },
      },
      // @ts-expect-error - TODO: (UI-7132) Fix this with a better generic Security type for securityFieldProperty
      pledgedCollateral: {
        field: 'DealInfo.PledgedCollateral',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.pledgedCollateral),
        type: 'size',
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.pledgedCollateral),
        params: {
          securityField: 'Symbol',
          securityFieldProperty: 'ProductDetails.CollateralAsset' satisfies Leaves<OTCFixedRateLoanProductTemplate>,
          transformValue: (value, params) =>
            isUnsecuredLoanDeal(params.data, params.context?.current?.securitiesBySymbol) ? EMPTY_SIZE_VALUE : value,
        },
      },
      term: {
        field: 'Symbol',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.term),
        type: 'securityText',
        initialWidth: 80,
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.term),
        params: {
          symbolField: 'Symbol',
          securityField: 'ProductDetails.Term',
        },
      },
      termType: {
        field: 'Symbol',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.termType),
        type: 'securityText',
        initialWidth: 90,
        description: appendTooltipWhenFeatureDisabled(
          getColumnTooltip(fixedRateLoanFieldDefinitions.termType),
          enableLoanOpenTermSupport,
          OPEN_TERM_SUPPORT_COMING_SOON
        ),
        params: {
          symbolField: 'Symbol',
          securityField: 'ProductDetails.TermType',
          getLabel: (value: string | undefined) => {
            if (value === LoanQuoteTerminationTypeEnum.OpenTerm) {
              return 'Open';
            }
            return 'Fixed';
          },
        },
      },
      expirationDate: {
        field: 'DealInfo.ExpiryTime',
        type: 'date',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.maturityDate),
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.maturityDate),
        params: {
          customFormatter: formatOtcDealBlotterLocalDateTime,
        },
        tooltipValueGetter: ({ value, data }) =>
          formatOtcDealBookingTimezoneTooltip(value, data?.DealInfo.SettleCutoffTimezone ?? 'UTC'),
      },
      settlementDate: {
        field: 'DealInfo.SettleTime',
        type: 'date',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.settleTime),
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.settleTime),
        params: {
          customFormatter: formatOtcDealBlotterLocalDateTime,
        },
        tooltipValueGetter: ({ value, data }) =>
          formatOtcDealBookingTimezoneTooltip(value, data?.DealInfo.SettleCutoffTimezone ?? 'UTC'),
      },
      productTemplate: getProductTemplateBlotterColumn({
        description: 'Product Template Symbol used to generate the deal',
      }),

      counterparty: {
        field: 'Counterparty',
        type: 'counterparty',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.counterparty),
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.counterparty),
      },
      side: {
        field: 'DealInfo.Side',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.loanSide),
        type: 'text',
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.loanSide),
      },
      loanAmount: {
        field: 'DealInfo.LoanAmount',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.loanAmount),
        type: 'size',
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.loanAmount),
        params: {
          valueGetter: (params: ValueGetterParams<OTCDealData>) => {
            const dealData = params.data;
            if (!dealData || !params.context?.current) {
              return { value: '', currency: '' };
            }
            if (!isFixedRateLoanDeal(dealData) && !isFloatingRateLoanDeal(dealData)) {
              return { value: '', currency: '' };
            }
            const dealSecurity = params.context.current.securitiesBySymbol?.get(dealData.Symbol ?? '');
            if (!isFixedRateLoanSecurity(dealSecurity) && !isFloatingRateLoanSecurity(dealSecurity)) {
              return { value: '', currency: '' };
            }
            const display = getOtcDealLoanAmountDisplayValue(dealData, dealSecurity);
            return { value: display.value, currency: display.currency };
          },
        },
      },
      rate: {
        field: 'DealInfo.Rate',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.rate),
        type: 'percent',
        initialWidth: 100,
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.rate),
      },
      paymentPeriod: {
        field: 'Symbol',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.paymentPeriod),
        type: 'securityText',
        initialWidth: 120,
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.paymentPeriod),
        params: {
          getLabel: formatPaymentPeriod,
          symbolField: 'Symbol',
          securityField: 'ProductDetails.PaymentPeriod',
        },
      },

      subAccount: {
        field: 'SubAccount',
        type: 'subAccount',
        title: 'Sub Account(s)',
        description: 'The internal account used to segregate trades and deals',
      },
      user: otcDealUserColumn,
      settlementCycle: {
        field: 'DealInfo.SettleCycle',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.settlementCycle),
        type: 'text',
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.settlementCycle),
        params: {
          getLabel: formatSettlementCycle,
        },
      },
      loanId: {
        field: 'ID',
        type: 'id',
        initialWidth: 100,
        title: 'Loan ID',
        description: 'The unique Talos identifier for the loan',
      },
      businessDayConvention: {
        field: 'DealInfo.BusinessDayHolidayConvention',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.businessDayConvention),
        type: 'text',
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.businessDayConvention),
      },
      holidayCalendars: {
        field: 'DealInfo.HolidayCalendars',
        title: getColumnTitle(fixedRateLoanFieldDefinitions.holidayCalendars),
        type: 'text',
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.holidayCalendars),
        params: {
          getLabel: formatHolidayCalendars,
        },
      },

      allocationCurrency: {
        field: 'Allocation.Currency',
        title: 'Allocation Currency',
        type: 'currency',
        description: 'Currency used for allocation',
      },
      marketAccount: {
        field: 'MarketAccount',
        title: 'Market Account',
        type: 'marketAccount',
        description: 'Market account associated with the loan',
      },
      timestamp: {
        field: 'Timestamp',
        title: 'Updated At',
        type: 'date',
        params: { milliseconds: true },
      },
    } as const satisfies Record<string, ColumnDef<OTCDealData>>;

    return mapping;
  }, [enableLoanOpenTermSupport]);

  return columnMapping;
}
